import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plane, Hotel, Utensils, Compass, Landmark, Home as HomeIcon, Sun, Sunrise, Award, Sparkles, Music, Waves as WavesIcon, Flag, Mountain, Camera, ShoppingBag, Wine, Bird } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SA_IMAGES = {
  hero: 'https://images.pexels.com/photos/1502311/pexels-photo-1502311.jpeg',
  joburg: 'https://images.unsplash.com/photo-1636706519609-988babca3dd5',
  apartheid: 'https://images.unsplash.com/photo-1604614765191-02ea6bdb941b',
  kruger1: 'https://images.unsplash.com/photo-1534476478164-b15fec4f091c',
  kruger2: 'https://images.unsplash.com/photo-1554990772-0bea55d510d5',
  kruger3: 'https://images.pexels.com/photos/34780131/pexels-photo-34780131.jpeg',
  pilanesberg1: 'https://images.unsplash.com/photo-1758881534639-709146239bd1',
  pilanesberg2: 'https://images.unsplash.com/photo-1596219323962-b30ec6be7576',
  pilanesberg3: 'https://images.unsplash.com/photo-1566296524462-e0a341bf65e6',
  sunCity: 'https://images.pexels.com/photos/32234902/pexels-photo-32234902.jpeg',
  capeTown6: 'https://images.unsplash.com/photo-1709031123386-581f06e5cc9a',
  capeTown7: 'https://images.unsplash.com/photo-1626894169601-482d26b23f35',
  capeTown8: 'https://images.unsplash.com/photo-1634636209285-8b3b16694560',
  capeTown9: 'https://images.unsplash.com/photo-1570527141186-e391a3914c42',
  capeTown10: 'https://images.pexels.com/photos/12476720/pexels-photo-12476720.jpeg',
};

function DaySection({ dayNum, title, description, image, items, quote }) {
  return (
    <div className="mb-16">
      <div className="inline-block px-4 py-2 bg-accent/10 border border-accent/30 rounded-full mb-4">
        <span className="text-accent font-bold">DIA {dayNum}</span>
      </div>
      <h3 className="text-3xl font-bold text-primary mb-2">{title}</h3>
      <p className="text-muted-foreground mb-6 italic">{description}</p>

      <div className="mb-8">
        <img src={image} alt={title} className="w-full h-96 object-cover rounded-lg mb-6 shadow-md" />
      </div>

      <div className="space-y-4 mb-8">
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} className="flex gap-4">
              <Icon className="text-accent flex-shrink-0" size={24} />
              <div>
                <h4 className="font-bold text-lg text-primary">{item.title}</h4>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-lg italic text-primary bg-accent/5 border-l-4 border-accent pl-4 py-3 rounded">“{quote}”</p>
    </div>
  );
}

export default function SouthAfrica() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="relative h-96 bg-cover bg-center" style={{ backgroundImage: `url(${SA_IMAGES.hero})` }}>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">ROTEIRO ÁFRICA DO SUL</h1>
          <p className="text-xl text-white">A Jornada Definitiva: História, Safari e Natureza</p>
        </div>
      </div>

      <div className="container py-6">
        <button onClick={() => navigate('/#destinations')} className="flex items-center gap-2 text-primary hover:text-accent transition-colors font-semibold">
          <ArrowLeft size={20} /> Voltar aos Destinos
        </button>
      </div>

      <div className="bg-slate-50 py-12">
        <div className="container max-w-3xl text-center">
          <p className="text-lg text-muted-foreground">
            A porta de entrada para a África do Sul, cidade onde história e modernidade se encontram, oferecendo contexto essencial sobre a nação.
          </p>
        </div>
      </div>

      <div className="container py-16 max-w-4xl">
        <h2 className="text-4xl font-bold text-primary mb-12 text-center">DIAS 1 E 2: JOANESBURGO - HISTÓRIA E CULTURA</h2>

        <DaySection dayNum="1" title="Chegada e Adaptação" description="Bem-vindo à porta de entrada da África do Sul" image={SA_IMAGES.joburg}
          items={[
            { icon: Plane, title: 'Chegada em Joanesburgo', desc: 'Transfer e recepção no aeroporto.' },
            { icon: Hotel, title: 'Check-in em Sandton', desc: 'Hospedagem na região segura e sofisticada.' },
            { icon: Compass, title: 'Passeio Leve', desc: 'Visita rápida à Nelson Mandela Square.' },
            { icon: Utensils, title: 'Jantar de Boas-vindas', desc: 'Experiência gastronômica local.' },
          ]}
          quote="Chegue com estilo e adapte-se ao ritmo da África do Sul em um ambiente de luxo." />

        <DaySection dayNum="2" title="Imersão Histórica" description="Compreenda a história e a cultura do país" image={SA_IMAGES.apartheid}
          items={[
            { icon: Landmark, title: 'Museu do Apartheid', desc: 'Visita para entender a história do país.' },
            { icon: HomeIcon, title: 'Soweto Tour', desc: 'Passeio pelo bairro histórico.' },
            { icon: HomeIcon, title: 'Casa de Mandela', desc: 'Parada na antiga residência de Mandela.' },
            { icon: Flag, title: 'Hector Pieterson Memorial', desc: 'Homenagem ao levante de 1976.' },
          ]}
          quote="Uma jornada pela história que moldou a nação, compreendendo seu passado para apreciar seu presente." />
      </div>

      <div className="bg-slate-50 py-16">
        <div className="container max-w-4xl">
          <h2 className="text-4xl font-bold text-primary mb-12 text-center">DIAS 3-5: ESCOLHA SUA EXPERIÊNCIA DE SAFARI</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">Kruger National Park</h3>
              <p className="text-muted-foreground mb-6">A experiência de safari mais selvagem e completa do continente. Ideal para quem busca imersão total na natureza e o máximo de exclusividade.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><span className="text-accent font-bold">✓</span><span>Big Five Completo</span></li>
                <li className="flex items-center gap-2"><span className="text-accent font-bold">✓</span><span>Experiência Premium</span></li>
                <li className="flex items-center gap-2"><span className="text-accent font-bold">✓</span><span>Selvagem e Exclusivo</span></li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-primary mb-4">Pilanesberg National Park</h3>
              <p className="text-muted-foreground mb-6">Praticidade e encanto a poucas horas de Joanesburgo. Perfeito para quem quer otimizar o tempo sem abrir mão da beleza da vida selvagem.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><span className="text-accent font-bold">✓</span><span>Prático e Acessível</span></li>
                <li className="flex items-center gap-2"><span className="text-accent font-bold">✓</span><span>Livre de Malária</span></li>
                <li className="flex items-center gap-2"><span className="text-accent font-bold">✓</span><span>Cratera Vulcânica Única</span></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-16 max-w-4xl">
        <h2 className="text-4xl font-bold text-primary mb-12 text-center">OPÇÃO 1: A EXPERIÊNCIA SELVAGEM - KRUGER NATIONAL PARK</h2>
        <DaySection dayNum="3" title="Chegada ao Coração da Savana" description="Voo de Joanesburgo para Nelspruit ou Hoedspruit" image={SA_IMAGES.kruger1}
          items={[
            { icon: Plane, title: 'Voo para Nelspruit ou Hoedspruit', desc: 'Transfer para o lodge e acomodação.' },
            { icon: Sunrise, title: 'Primeiro Game Drive', desc: 'Safari ao pôr do sol para saudar a vida selvagem.' },
            { icon: Hotel, title: 'Repouso Premium', desc: 'Refeição e descanso no lodge.' },
          ]}
          quote="Prepare-se para o encontro mais esperado: a vida selvagem em sua forma mais pura." />

        <DaySection dayNum="4" title="Em Busca dos Big Five" description="Safaris completos ao amanhecer e ao entardecer" image={SA_IMAGES.kruger2}
          items={[
            { icon: Sunrise, title: 'Safari ao Amanhecer', desc: 'Observação matinal da fauna.' },
            { icon: Sun, title: 'Safari ao Entardecer', desc: 'Experiência ao pôr do sol.' },
            { icon: Compass, title: 'Exploração da Imensidão', desc: 'Busca por leões, leopardos, elefantes, búfalos e rinocerontes.' },
            { icon: Utensils, title: 'Refeições no Parque', desc: 'Almoço e jantar gourmet.' },
          ]}
          quote="Acompanhado por guias experientes, explore a imensidão do parque em busca dos Big Five." />

        <DaySection dayNum="5" title="Despedida da Natureza" description="Último safari matinal antes do retorno" image={SA_IMAGES.kruger3}
          items={[
            { icon: Sunrise, title: 'Último Safari Matinal', desc: 'Captura dos últimos momentos na savana.' },
            { icon: Plane, title: 'Retorno para Joanesburgo', desc: 'Voo direto ou via Cidade do Cabo.' },
            { icon: Sparkles, title: 'Memórias Inesquecíveis', desc: 'Leve consigo momentos que durarão para sempre.' },
          ]}
          quote="Encerre sua jornada com a certeza de ter vivido a experiência de safari mais autêntica do continente." />
      </div>

      <div className="bg-slate-50 py-16">
        <div className="container max-w-4xl">
          <h2 className="text-4xl font-bold text-primary mb-12 text-center">OPÇÃO 2: PRATICIDADE - PILANESBERG NATIONAL PARK</h2>
          <DaySection dayNum="3" title="Transfer e Primeiro Safari" description="Saída de Joanesburgo (3h-4h de estrada). Check-in no lodge e safari fotográfico ao entardecer." image={SA_IMAGES.pilanesberg1}
            items={[
              { icon: Plane, title: 'Saída de Joanesburgo', desc: 'Estrada cênica de 3-4h até o lodge.' },
              { icon: Hotel, title: 'Check-in Lodge', desc: 'Acomodação confortável dentro do parque.' },
              { icon: Sun, title: 'Safari ao Entardecer', desc: 'Primeira aproximação com a vida selvagem local.' },
            ]}
            quote="Praticidade sem abrir mão da beleza selvagem sul-africana." />
          <DaySection dayNum="4" title="Safari Completo" description="Dois game drives (manhã e tarde) para observação da rica fauna em uma cratera vulcânica extinta." image={SA_IMAGES.pilanesberg2}
            items={[
              { icon: Sunrise, title: 'Game Drive Matinal', desc: 'Fauna ativa nas primeiras horas.' },
              { icon: Sun, title: 'Game Drive Vespertino', desc: 'Observação de predadores em atividade.' },
              { icon: Utensils, title: 'Refeições no Lodge', desc: 'Gastronomia local e internacional.' },
            ]}
            quote="Uma cratera vulcânica extinta transformada em santuário de vida selvagem." />
          <DaySection dayNum="5" title="Safari Leve e Retorno" description="Último game drive ao amanhecer seguido de café da manhã e retorno para Joanesburgo." image={SA_IMAGES.pilanesberg3}
            items={[
              { icon: Sunrise, title: 'Game Drive de Despedida', desc: 'Manhã final em contato com a natureza.' },
              { icon: Plane, title: 'Retorno a Joanesburgo', desc: 'Volta confortável para a cidade.' },
            ]}
            quote="A melhor opção para quem busca otimizar o investimento sem abrir mão da experiência autêntica." />
        </div>
      </div>

      <div className="container py-16 max-w-4xl">
        <div className="relative overflow-hidden text-white p-12 rounded-2xl" style={{ backgroundImage: `url(${SA_IMAGES.sunCity})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, hsl(210 67% 25% / 0.88), hsl(16 100% 40% / 0.78))' }}></div>
          <div className="relative z-10">
            <h2 className="text-4xl font-bold mb-6 text-white">O REINO DO ENTRETENIMENTO</h2>
            <h3 className="text-3xl font-bold mb-4 text-white">The Palace of the Lost City</h3>
            <p className="text-xl mb-6">Um Ícone de Luxo em Sun City</p>
            <p className="mb-6">Inspirado em uma lenda de uma civilização perdida, The Palace é um dos hotéis mais extraordinários do mundo, oferecendo uma experiência de realeza no coração da savana.</p>
            <p className="mb-8">Localizado no complexo de Sun City, o resort combina arquitetura monumental com entretenimento de classe mundial, sendo o destino perfeito para quem busca sofisticação e diversão em um só lugar.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-3"><Sparkles size={24} /><div><h4 className="font-bold text-white">Arquitetura Monumental</h4><p>Inspirada em civilizações perdidas</p></div></div>
              <div className="flex gap-3"><Music size={24} /><div><h4 className="font-bold text-white">Entretenimento de Classe Mundial</h4><p>Shows, restaurantes e atividades</p></div></div>
              <div className="flex gap-3"><WavesIcon size={24} /><div><h4 className="font-bold text-white">Piscinas e Spa Premium</h4><p>Relaxamento total</p></div></div>
              <div className="flex gap-3"><Award size={24} /><div><h4 className="font-bold text-white">Campo de Golf</h4><p>Experiência de golfe única</p></div></div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 py-16">
        <div className="container max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-accent/10 border border-accent/30 rounded-full mb-4">
              <span className="text-accent font-bold">EXTENSÃO EXCLUSIVA</span>
            </div>
            <h2 className="text-4xl font-bold text-primary mb-4">DIAS 6-10: CIDADE DO CABO</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Após vivenciar a alma selvagem da savana, siga rumo à Cidade do Cabo para descobrir uma das cidades mais belas do mundo, com Table Mountain, praias icônicas, vinícolas premiadas e uma cultura vibrante.
            </p>
          </div>

          <DaySection dayNum="6" title="Rumo à Cidade do Cabo" description="Voo de Joanesburgo até a Mother City" image={SA_IMAGES.capeTown6}
            items={[
              { icon: Plane, title: 'Voo Airlink JNB → CPT', desc: 'Decolagem às 12h20 de Joanesburgo com pouso em Cape Town às 14h35, sobrevoando paisagens espetaculares.' },
              { icon: Compass, title: 'Transfer para o Hotel', desc: 'Recepção no aeroporto e transfer privativo até o Capetonian Hotel, na região central da cidade.' },
              { icon: Hotel, title: 'Capetonian Hotel', desc: 'Check-in em hotel confortável com café da manhã incluso, próximo ao V&A Waterfront.' },
              { icon: Camera, title: 'Primeiras Impressões', desc: 'Tarde livre para adaptação e primeiro contato com a paisagem única entre montanhas e oceano.' },
            ]}
            quote="A Cidade do Cabo recebe você com o abraço da Table Mountain e a brisa do Atlântico." />

          <DaySection dayNum="7" title="Explorando a Cidade do Cabo" description="Descubra os principais cartões-postais da cidade" image={SA_IMAGES.capeTown7}
            items={[
              { icon: Mountain, title: 'Table Mountain', desc: 'Suba de teleférico até o topo da montanha mais icônica da África para vistas panorâmicas de 360°.' },
              { icon: Compass, title: 'V&A Waterfront', desc: 'Passeie pelo porto histórico revitalizado, com lojas, restaurantes, museus e vista para o oceano.' },
              { icon: HomeIcon, title: 'Bairro Bo-Kaap', desc: 'Caminhe pelas ruas de paralelepípedos entre casas coloridas do histórico bairro malaio-muçulmano.' },
              { icon: Utensils, title: 'Jantar Cape Malay', desc: 'Sugestão: experimente a culinária local, fusão de sabores africanos, asiáticos e europeus.' },
            ]}
            quote="Poucas cidades no mundo oferecem tanta diversidade cultural e natural em um único dia." />

          <DaySection dayNum="8" title="Península do Cabo & Boulders Beach" description="Aventura pela costa mais dramática da África" image={SA_IMAGES.capeTown8}
            items={[
              { icon: Bird, title: 'Boulders Beach', desc: 'Encontro imperdível com a colônia de pinguins-africanos em uma praia paradisíaca de areia branca.' },
              { icon: Mountain, title: 'Cabo da Boa Esperança', desc: 'Visite o ponto onde as águas do Atlântico e do Índico se encontram, com falésias espetaculares.' },
              { icon: Compass, title: 'Chapman’s Peak Drive', desc: 'Uma das estradas costeiras mais belas do mundo, com mirantes de tirar o fôlego.' },
              { icon: Wine, title: 'Rota dos Vinhos (opcional)', desc: 'Alternativa: visita às vinícolas de Stellenbosch ou Constantia, com degustação de rótulos premiados.' },
            ]}
            quote="Da vida selvagem marinha às vinícolas centenárias, o Cabo entrega experiências únicas em um só dia." />

          <DaySection dayNum="9" title="Últimos Momentos" description="Dia livre para curtir a cidade no seu ritmo" image={SA_IMAGES.capeTown9}
            items={[
              { icon: ShoppingBag, title: 'Compras & Souvenirs', desc: 'Explore o V&A Waterfront ou o Watershed Market para levar artesanato, joias e lembranças autênticas.' },
              { icon: Compass, title: 'Kirstenbosch Gardens', desc: 'Sugestão: visite um dos jardins botânicos mais bonitos do mundo, aos pés da Table Mountain.' },
              { icon: Camera, title: 'Bo-Kaap Fotográfico', desc: 'Retorne ao bairro colorido para capturar as fotos mais icônicas ao entardecer.' },
              { icon: Utensils, title: 'Jantar de Despedida', desc: 'Encerre a viagem em um restaurante premiado com vista para o oceano ou para a montanha.' },
            ]}
            quote="Aproveite cada instante — a Cidade do Cabo tem o dom de deixar sempre a sensação de que faltou tempo." />

          <DaySection dayNum="10" title="Retorno ao Brasil" description="Embarque com o coração cheio de memórias" image={SA_IMAGES.capeTown10}
            items={[
              { icon: Utensils, title: 'Café da Manhã & Check-out', desc: 'Últimos momentos no hotel antes da despedida da Mother City.' },
              { icon: Plane, title: 'Transfer para o Aeroporto', desc: 'Deslocamento privativo até o aeroporto internacional da Cidade do Cabo.' },
              { icon: Plane, title: 'Voo LATAM CPT → GRU', desc: 'Embarque às 15h50 com destino a Guarulhos, chegada às 19h50 do dia seguinte.' },
              { icon: Sparkles, title: 'Memórias Eternas', desc: 'Encerramento de uma viagem inesquecível pela África do Sul — do safari selvagem ao Cabo da Boa Esperança.' },
            ]}
            quote="Você volta para casa, mas uma parte de você fica para sempre entre a savana e o oceano da África do Sul." />
        </div>
      </div>

      <div className="bg-slate-50 py-16">
        <div className="container max-w-4xl">
          <h2 className="text-4xl font-bold text-primary mb-12 text-center">O QUE ESTÁ INCLUÍDO</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              ['Voos Internacionais', 'Passagens aéreas de ida e volta', Plane],
              ['Hospedagem Premium', 'Hotéis 4 e 5 estrelas selecionados', Hotel],
              ['Transfers e Transportes', 'Todos os deslocamentos inclusos', Compass],
              ['Safaris Guiados', 'Game drives com guias experientes', Sun],
              ['Ingressos e Atividades', 'Todas as atrações e passeios', Sparkles],
              ['Refeições Gourmet', 'Café da manhã, almoço e jantar', Utensils],
            ].map(([t, d, Icon], i) => (
              <div key={i} className="bg-white p-6 rounded-lg border border-border">
                <h3 className="text-xl font-bold text-primary mb-3 flex items-center gap-2"><Icon size={22} className="text-accent" /> {t}</h3>
                <p className="text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative text-white py-16">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, hsl(210 67% 35%), hsl(210 60% 25%))' }}></div>
        <div className="relative container max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Pronto para Viver Esta Experiência?</h2>
          <p className="text-xl mb-8">Entre em contato conosco e customize seu roteiro</p>
          <a href="https://wa.me/5548991904829?text=Olá! Gostaria de orçar o roteiro premium da África do Sul." target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-primary font-bold px-8 py-3 rounded-lg hover:bg-slate-100 transition">
            Solicitar Orçamento via WhatsApp
          </a>
        </div>
      </div>

      <section className="py-8 bg-slate-50">
        <div className="container text-center text-muted-foreground text-sm">
          <p>PROPOSTA EXCLUSIVA | 2026</p>
          <p>@jmtravel.oficial | CNPJ 64.418.580/0001-14</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
